/**
 * GenericTestModal - Universal test/preview framework for all controls
 *
 * Features:
 * - Test Tool: Test control logic with sample data
 * - Preview Events: View sample event structures
 * - Control-specific sample generation
 */
import React, { useState } from 'react';
import { Modal, Tabs, Card, Button, Select, Alert, Table, Tag, Spin, message } from 'antd';
import {
  PlayCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';

const { TabPane } = Tabs;
const { Option } = Select;

// Sample event generators for each control
const SAMPLE_GENERATORS = {
  CTRL_01: () => ({
    event_type: 'model_invocation',
    model: 'gpt-4',
    provider: 'openai',
    timestamp: new Date().toISOString(),
    event_metadata: {
      prompt: 'User John Doe (SSN: 123-45-6789, email: john@example.com) requested account balance',
      response: 'Account balance for account ending in 4567 is $5,432.21',
      pii_detected: ['ssn', 'email', 'account_number'],
      pii_masked: true,
      detection_method: 'regex'
    }
  }),

  CTRL_02: () => ({
    event_type: 'model_deployment',
    model: 'fraud-detection-v3',
    provider: 'custom',
    timestamp: new Date().toISOString(),
    event_metadata: {
      use_case: 'credit_scoring',
      risk_level: 'high',
      risk_classification: 'high',
      impact_assessment_completed: true,
      human_oversight: true,
      deployment_environment: 'production'
    }
  }),

  CTRL_03: () => ({
    event_type: 'data_collection',
    model: 'recommendation-engine',
    provider: 'custom',
    timestamp: new Date().toISOString(),
    event_metadata: {
      data_purpose: 'training',
      collection_method: 'web_scraping',
      user_consent: true,
      data_retention_days: 365,
      purpose_documented: true
    }
  }),

  CTRL_04: () => ({
    event_type: 'data_ingestion',
    model: 'sentiment-analyzer',
    provider: 'huggingface',
    timestamp: new Date().toISOString(),
    event_metadata: {
      data_source: 's3://ml-data/training/reviews.csv',
      data_provenance: 'kaggle_dataset',
      lineage_tracked: true,
      data_quality_score: 0.89,
      metadata_complete: true
    }
  }),

  CTRL_05: () => ({
    event_type: 'model_training',
    model: 'customer-churn-predictor',
    provider: 'aws',
    timestamp: new Date().toISOString(),
    event_metadata: {
      data_sources: ['s3://data/customers.csv', 's3://data/transactions.csv'],
      data_minimized: true,
      features_relevant: true,
      unnecessary_features_removed: ['middle_name', 'pet_name'],
      retention_policy_applied: true
    }
  }),

  CTRL_06: () => ({
    event_type: 'model_monitoring',
    model: 'image-classifier-v2',
    provider: 'custom',
    timestamp: new Date().toISOString(),
    event_metadata: {
      accuracy: 0.94,
      precision: 0.92,
      recall: 0.91,
      f1_score: 0.915,
      latency_ms: 145,
      throughput: 1200,
      error_rate: 0.02,
      baseline_accuracy: 0.96
    }
  }),

  CTRL_07: () => ({
    event_type: 'fairness_test',
    model: 'hiring-assistant',
    provider: 'custom',
    timestamp: new Date().toISOString(),
    event_metadata: {
      test_type: 'demographic_parity',
      protected_attributes: ['gender', 'race', 'age'],
      fairness_metrics: {
        demographic_parity: 0.12,
        equalized_odds: 0.08,
        disparate_impact: 0.85
      },
      bias_detected: true,
      mitigation_applied: false
    }
  }),

  CTRL_08: () => ({
    event_type: 'human_review',
    model: 'loan-approval-system',
    provider: 'custom',
    timestamp: new Date().toISOString(),
    event_metadata: {
      decision_type: 'loan_approval',
      ai_recommendation: 'approve',
      human_reviewer: 'reviewer_42',
      human_decision: 'reject',
      override_reason: 'Insufficient collateral documentation',
      review_duration_minutes: 15
    }
  }),

  CTRL_09: () => ({
    event_type: 'data_transfer',
    model: 'analytics-platform',
    provider: 'gcp',
    timestamp: new Date().toISOString(),
    event_metadata: {
      source_region: 'us-east-1',
      destination_region: 'eu-west-1',
      data_classification: 'personal',
      cross_border_transfer: true,
      legal_basis: 'standard_contractual_clauses',
      encryption_in_transit: true
    }
  }),

  CTRL_10: () => ({
    event_type: 'model_update',
    model: 'content-moderator',
    provider: 'openai',
    timestamp: new Date().toISOString(),
    event_metadata: {
      version: 'v2.3.0',
      update_type: 'incremental',
      impact_assessment_completed: true,
      rollback_plan_exists: true,
      testing_completed: true,
      approval_obtained: true
    }
  })
};

// Control-specific test scenarios
const TEST_SCENARIOS = {
  CTRL_01: [
    { label: 'SSN + Email Detection', value: 'pii_ssn_email' },
    { label: 'Credit Card Detection', value: 'pii_credit_card' },
    { label: 'No PII (Clean)', value: 'no_pii' },
    { label: 'Masked PII', value: 'masked_pii' }
  ],
  CTRL_02: [
    { label: 'High-Risk Use Case', value: 'high_risk' },
    { label: 'Prohibited Use Case', value: 'prohibited' },
    { label: 'Limited Risk', value: 'limited_risk' },
    { label: 'Minimal Risk', value: 'minimal_risk' }
  ],
  CTRL_06: [
    { label: 'Performance Degradation', value: 'degradation' },
    { label: 'Stable Performance', value: 'stable' },
    { label: 'Drift Detected', value: 'drift' },
    { label: 'Missing Metrics', value: 'missing_metrics' }
  ],
  CTRL_07: [
    { label: 'Bias Detected', value: 'bias_detected' },
    { label: 'No Bias', value: 'no_bias' },
    { label: 'High Disparate Impact', value: 'high_impact' },
    { label: 'Missing Protected Attributes', value: 'missing_attrs' }
  ],
  default: [
    { label: 'Pass Scenario', value: 'pass' },
    { label: 'Fail Scenario', value: 'fail' },
    { label: 'Warning Scenario', value: 'warning' }
  ]
};

function GenericTestModal({ visible, onClose, controlCode, controlTitle, projectId }) {
  const [activeTab, setActiveTab] = useState('test');
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sampleEvent, setSampleEvent] = useState(null);

  const scenarios = TEST_SCENARIOS[controlCode] || TEST_SCENARIOS.default;

  const generateSampleEvent = () => {
    const generator = SAMPLE_GENERATORS[controlCode];
    if (generator) {
      const event = generator();
      setSampleEvent(event);
      return event;
    }
    return null;
  };

  const runTest = async () => {
    if (!selectedScenario) {
      message.warning('Please select a test scenario');
      return;
    }

    setLoading(true);
    try {
      const event = generateSampleEvent();

      // Simulate API call - in production this would call the actual evaluator
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mock test result
      const mockResult = {
        status: selectedScenario === 'pass' ? 'pass' : selectedScenario.includes('no_') ? 'pass' : 'fail',
        confidence: Math.floor(Math.random() * 20) + 80,
        rationale: `Test scenario '${scenarios.find(s => s.value === selectedScenario)?.label}' executed successfully`,
        violations: selectedScenario.includes('fail') || selectedScenario.includes('bias') ?
          ['Sample violation detected in test scenario'] : [],
        statistics: {
          events_processed: 1,
          pass_count: selectedScenario === 'pass' ? 1 : 0,
          fail_count: selectedScenario === 'pass' ? 0 : 1
        }
      };

      setTestResult(mockResult);
      message.success('Test completed successfully');
    } catch (error) {
      message.error('Test failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const eventColumns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      width: '30%',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value) => (
        <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
        </code>
      )
    }
  ];

  const flattenEventData = (event) => {
    if (!event) return [];
    const flattened = [];
    Object.entries(event).forEach(([key, value]) => {
      if (key === 'event_metadata' && typeof value === 'object') {
        Object.entries(value).forEach(([metaKey, metaValue]) => {
          flattened.push({
            field: `event_metadata.${metaKey}`,
            value: metaValue
          });
        });
      } else {
        flattened.push({ field: key, value });
      }
    });
    return flattened;
  };

  return (
    <Modal
      title={`Test & Preview: ${controlCode} - ${controlTitle}`}
      visible={visible}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <PlayCircleOutlined /> Test Tool
            </span>
          }
          key="test"
        >
          <Card>
            <Alert
              message="Control Testing"
              description={`Test ${controlCode} control logic with different scenarios. Select a scenario and click Run Test to see how the control evaluates sample data.`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Select Test Scenario:
              </label>
              <Select
                value={selectedScenario}
                onChange={setSelectedScenario}
                style={{ width: '100%' }}
                placeholder="Choose a test scenario"
              >
                {scenarios.map(scenario => (
                  <Option key={scenario.value} value={scenario.value}>
                    {scenario.label}
                  </Option>
                ))}
              </Select>
            </div>

            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={runTest}
              loading={loading}
              disabled={!selectedScenario}
              style={{ marginBottom: '16px' }}
            >
              Run Test
            </Button>

            {testResult && (
              <Card
                title="Test Result"
                style={{ marginTop: '16px' }}
                extra={
                  testResult.status === 'pass' ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">PASS</Tag>
                  ) : (
                    <Tag icon={<CloseCircleOutlined />} color="error">FAIL</Tag>
                  )
                }
              >
                <div style={{ marginBottom: '12px' }}>
                  <strong>Confidence:</strong> {testResult.confidence}%
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Rationale:</strong> {testResult.rationale}
                </div>
                {testResult.violations && testResult.violations.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Violations:</strong>
                    <ul>
                      {testResult.violations.map((v, idx) => (
                        <li key={idx} style={{ color: '#d32f2f' }}>{v}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {testResult.statistics && (
                  <div>
                    <strong>Statistics:</strong>
                    <pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', marginTop: '8px' }}>
                      {JSON.stringify(testResult.statistics, null, 2)}
                    </pre>
                  </div>
                )}
              </Card>
            )}
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <EyeOutlined /> Preview Events
            </span>
          }
          key="preview"
        >
          <Card>
            <Alert
              message="Sample Event Structure"
              description={`View the expected event structure for ${controlCode}. This shows what data should be sent to the TrustGrid API for this control.`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={generateSampleEvent}
              style={{ marginBottom: '16px' }}
            >
              Generate Sample Event
            </Button>

            {sampleEvent && (
              <>
                <Card
                  title="Event Structure"
                  extra={
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(JSON.stringify(sampleEvent, null, 2))}
                    >
                      Copy JSON
                    </Button>
                  }
                  style={{ marginBottom: '16px' }}
                >
                  <Table
                    dataSource={flattenEventData(sampleEvent)}
                    columns={eventColumns}
                    rowKey="field"
                    pagination={false}
                    size="small"
                    bordered
                  />
                </Card>

                <Card title="Full JSON">
                  <pre style={{
                    background: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px'
                  }}>
                    {JSON.stringify(sampleEvent, null, 2)}
                  </pre>
                </Card>
              </>
            )}
          </Card>
        </TabPane>
      </Tabs>
    </Modal>
  );
}

export default GenericTestModal;
